/**
 * useAnimationStates Hook
 *
 * Manages animation states for RecipientItem component.
 * Coordinates pop-in, load-in, and delete animations.
 *
 * Animation Sequence:
 * - Pop animation: Triggered for newly added items (scales up)
 * - Load animation: Triggered on initial page load (fades in)
 * - Delete animation: Two phases - slide out, then collapse
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { UseAnimationStatesReturn, EmailRecipient } from '../types';
import {
  POP_ANIMATION_DURATION,
  DELETE_ANIMATION_DURATION,
  LOAD_ANIMATION_DURATION,
} from '../constants';

interface UseAnimationStatesOptions {
  /** Whether this item is newly added */
  isNew?: boolean;
  /** Whether this item is loading in (initial page load) */
  isLoadingIn?: boolean;
  /** Whether this item is being deleted */
  isAnimatingOut?: boolean;
  /** The recipient for callbacks */
  recipient: EmailRecipient;
  /** Callback when delete animation completes */
  onDeleteAnimationComplete?: (recipient: EmailRecipient) => void;
}

export function useAnimationStates({
  isNew = false,
  isLoadingIn = false,
  isAnimatingOut = false,
  recipient,
  onDeleteAnimationComplete,
}: UseAnimationStatesOptions): UseAnimationStatesReturn {
  // Animation state flags
  const [isPopAnimating, setIsPopAnimating] = useState(isNew);
  const [isLoadAnimating, setIsLoadAnimating] = useState(isLoadingIn);
  const [isDeleteAnimating, setIsDeleteAnimating] = useState(false);

  // Internal state for delete animation phases
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Refs for cleanup
  const popTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deletePhase1Ref = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deletePhase2Ref = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popTimerRef.current) clearTimeout(popTimerRef.current);
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
      if (deletePhase1Ref.current) clearTimeout(deletePhase1Ref.current);
      if (deletePhase2Ref.current) clearTimeout(deletePhase2Ref.current);
    };
  }, []);

  // Handle pop-in animation
  useEffect(() => {
    if (isPopAnimating) {
      popTimerRef.current = setTimeout(() => {
        setIsPopAnimating(false);
      }, POP_ANIMATION_DURATION);

      return () => {
        if (popTimerRef.current) {
          clearTimeout(popTimerRef.current);
        }
      };
    }
  }, [isPopAnimating]);

  // Handle load-in animation
  useEffect(() => {
    if (isLoadAnimating) {
      loadTimerRef.current = setTimeout(() => {
        setIsLoadAnimating(false);
      }, LOAD_ANIMATION_DURATION);

      return () => {
        if (loadTimerRef.current) {
          clearTimeout(loadTimerRef.current);
        }
      };
    }
  }, [isLoadAnimating]);

  // Handle delete animation sequence (triggered by parent)
  useEffect(() => {
    if (isAnimatingOut && !isDeleteAnimating) {
      setIsDeleteAnimating(true);

      // Phase 1: Slide out (250ms)
      deletePhase1Ref.current = setTimeout(() => {
        setIsCollapsed(true);
      }, 250);

      // Phase 2: Collapse complete, trigger removal (250ms + 300ms)
      deletePhase2Ref.current = setTimeout(() => {
        onDeleteAnimationComplete?.(recipient);
      }, 250 + DELETE_ANIMATION_DURATION);

      return () => {
        if (deletePhase1Ref.current) clearTimeout(deletePhase1Ref.current);
        if (deletePhase2Ref.current) clearTimeout(deletePhase2Ref.current);
      };
    }
  }, [isAnimatingOut, isDeleteAnimating, recipient, onDeleteAnimationComplete]);

  // Trigger delete animation manually
  const triggerDeleteAnimation = useCallback(() => {
    setIsDeleteAnimating(true);
  }, []);

  // Reset all animations
  const resetAnimations = useCallback(() => {
    setIsPopAnimating(false);
    setIsLoadAnimating(false);
    setIsDeleteAnimating(false);
    setIsCollapsed(false);

    if (popTimerRef.current) clearTimeout(popTimerRef.current);
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    if (deletePhase1Ref.current) clearTimeout(deletePhase1Ref.current);
    if (deletePhase2Ref.current) clearTimeout(deletePhase2Ref.current);
  }, []);

  return {
    // State
    isPopAnimating,
    isDeleteAnimating: isDeleteAnimating || isCollapsed,
    isLoadAnimating,
    // Actions
    triggerDeleteAnimation,
    resetAnimations,
  };
}
